import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, BaseModelObject, ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumDocumentFileType } from "./enums/enumDocumentFileType.generated";
import { EnumStockTakeStatus } from "./enums/enumStockTakeStatus.generated";

// Model Objects

export class StockTakeHeaderReadOnly extends ModelObject<StockTakeHeaderReadOnly> {

    private _completedAt: Instant | null = null;
    /**
    * Timestamp at which stock take was completed. (Null means not yet completed).
    */
    public get completedAt(): Instant | null { return this._completedAt }
    public set completedAt(value: Instant | null) {
      this._completedAt = value === undefined ? null : value;
    }
    /**
    * User Id of the creating user.
    */
    public createdByUserId: Guid = Guid.createEmpty();
    /**
    * Stock Take Number
    * Not Nullable
    * Preserves leading and trailing whitespce.
    */
    public number: string = "";
    /**
    * Number of lines which have a count entered by the user different to the frozen stock level, Ie requiring an adjustment.
    */
    public numberOfAdjustments: number = 0;
    /**
    * Number of lines which have a count entered by the user performing the stock take.
    */
    public numberOfEnteredCounts: number = 0;
    private _startedAt: Instant | null = null;
    /**
    * Timestamp at which stock take was started (and levels frozen). (Null means not yet started).
    */
    public get startedAt(): Instant | null { return this._startedAt }
    public set startedAt(value: Instant | null) {
      this._startedAt = value === undefined ? null : value;
    }
    /**
    * Timestamp at which the stock take was created on the system. Captured by system not user editable.
    */
    public createdAt: Instant = Instant.ofEpochMilli(0);
    private _cancelledAt: Instant | null = null;
    /**
    * Timestamp at which stock take was completed. (Null means not cancelled).
    */
    public get cancelledAt(): Instant | null { return this._cancelledAt }
    public set cancelledAt(value: Instant | null) {
      this._cancelledAt = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.completedAt = jsonSerializedObject.completedAt == null ? null : Instant.parse(jsonSerializedObject.completedAt);
          this.createdByUserId = Guid.parseOrEmpty(jsonSerializedObject.createdByUserId);
          this.number = jsonSerializedObject.number;
          this.numberOfAdjustments = jsonSerializedObject.numberOfAdjustments;
          this.numberOfEnteredCounts = jsonSerializedObject.numberOfEnteredCounts;
          this.startedAt = jsonSerializedObject.startedAt == null ? null : Instant.parse(jsonSerializedObject.startedAt);
          this.createdAt = Instant.parse(jsonSerializedObject.createdAt);
          this.cancelledAt = jsonSerializedObject.cancelledAt == null ? null : Instant.parse(jsonSerializedObject.cancelledAt);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.completedAt = this.completedAt == null ? null : this.completedAt.toString();
        toRet.createdByUserId = this.createdByUserId.toString();
        toRet.number = this.number;
        toRet.numberOfAdjustments = this.numberOfAdjustments;
        toRet.numberOfEnteredCounts = this.numberOfEnteredCounts;
        toRet.startedAt = this.startedAt == null ? null : this.startedAt.toString();
        toRet.createdAt = this.createdAt.toString();
        toRet.cancelledAt = this.cancelledAt == null ? null : this.cancelledAt.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "createdByUserId", rule: (v: any) => !v.isEmpty() || "StockTakeHeaderReadOnly.CreatedByUserId cannot be empty"},
            {field: "numberOfAdjustments", rule: (v: any) => v == null || v >= -2147483648 || "NumberOfAdjustments is not greater than minimum allowed value: -2147483648"},
            {field: "numberOfAdjustments", rule: (v: any) => v == null || v <= 2147483647 || "NumberOfAdjustments is above maximum allowed value: 2147483647"},
            {field: "numberOfEnteredCounts", rule: (v: any) => v == null || v >= -2147483648 || "NumberOfEnteredCounts is not greater than minimum allowed value: -2147483648"},
            {field: "numberOfEnteredCounts", rule: (v: any) => v == null || v <= 2147483647 || "NumberOfEnteredCounts is above maximum allowed value: 2147483647"},
    ];
}


export class StockTakeHeaderCalculated extends ModelObject<StockTakeHeaderCalculated> {

    public status: EnumStockTakeStatus = EnumStockTakeStatus.none;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.status = EnumStockTakeStatus.getByValue(jsonSerializedObject.status, this.status);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.status = this.status.value;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class StockTakeHeader extends ModelObject<StockTakeHeader> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Include Materials in the stock take. (Either this or IncludeProducts or both should be true).
    */
    public includeMaterials: boolean = false;
    /**
    * Include Products in the stock take. (Either this or IncludeMaterials or both should be true).
    */
    public includeProducts: boolean = false;
    /**
    * Notes
    * Not Nullable
    * Max Length : 100000
    */
    public notes: string = "";
    private _stockGroupIds: Guid[] | null = null;
    /**
    * Ids of the stock group being counted, if stock take is being done for limited stock groups.
    */
    public get stockGroupIds(): Guid[] | null { return this._stockGroupIds }
    public set stockGroupIds(value: Guid[] | null) {
      this._stockGroupIds = value === undefined ? null : value;
    }
    /**
    * Id of the stock location where stock is being counted.
    */
    public stockLocationId: Guid = Guid.createEmpty();
    private _supplierIds: Guid[] | null = null;
    /**
    * Ids of the suppliers whose stock items are being counted, if stock take is being done for limited suppliers.
    */
    public get supplierIds(): Guid[] | null { return this._supplierIds }
    public set supplierIds(value: Guid[] | null) {
      this._supplierIds = value === undefined ? null : value;
    }
    public readOnly: StockTakeHeaderReadOnly = new StockTakeHeaderReadOnly();
    /**
    * Reason
    * Not Nullable
    * Max Length : 100000
    */
    public reason: string = "";
    public calculated: StockTakeHeaderCalculated = new StockTakeHeaderCalculated();
    /**
    * Include stock items with zero quantity in the stock take.
    */
    public includeQuantitiesOfZero: boolean = false;
    /**
    * Include stock items with negative quantities in the stock take.
    */
    public includeNegativeQuantities: boolean = false;
    /**
    * Include stock items which are hidden in the stock take.
    */
    public includeHiddenItems: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.includeMaterials = jsonSerializedObject.includeMaterials;
          this.includeProducts = jsonSerializedObject.includeProducts;
          this.notes = jsonSerializedObject.notes;
          this.stockGroupIds = jsonSerializedObject.stockGroupIds == null ? null : jsonSerializedObject.stockGroupIds.map((v: any) => Guid.parseOrEmpty(v));
          this.stockLocationId = Guid.parseOrEmpty(jsonSerializedObject.stockLocationId);
          this.supplierIds = jsonSerializedObject.supplierIds == null ? null : jsonSerializedObject.supplierIds.map((v: any) => Guid.parseOrEmpty(v));
          this.readOnly = new StockTakeHeaderReadOnly(jsonSerializedObject.readOnly);
          this.reason = jsonSerializedObject.reason;
          this.calculated = new StockTakeHeaderCalculated(jsonSerializedObject.calculated);
          this.includeQuantitiesOfZero = jsonSerializedObject.includeQuantitiesOfZero;
          this.includeNegativeQuantities = jsonSerializedObject.includeNegativeQuantities;
          this.includeHiddenItems = jsonSerializedObject.includeHiddenItems;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.includeMaterials = this.includeMaterials;
        toRet.includeProducts = this.includeProducts;
        toRet.notes = this.notes;
        toRet.stockGroupIds = this.stockGroupIds == null ? null : this.stockGroupIds.map(v => v.toString());
        toRet.stockLocationId = this.stockLocationId.toString();
        toRet.supplierIds = this.supplierIds == null ? null : this.supplierIds.map(v => v.toString());
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        toRet.reason = this.reason;
        toRet.calculated = this.calculated.toJsonSerializedObject();
        toRet.includeQuantitiesOfZero = this.includeQuantitiesOfZero;
        toRet.includeNegativeQuantities = this.includeNegativeQuantities;
        toRet.includeHiddenItems = this.includeHiddenItems;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "StockTakeHeader.Id cannot be empty"},
            {field: "notes", rule: (v: any) => v == null || v.length <= 100000 || "Notes has too many characters (100000)"},
            {field: "stockLocationId", rule: (v: any) => !v.isEmpty() || "StockTakeHeader.StockLocationId cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
            {field: "reason", rule: (v: any) => v == null || v.length <= 100000 || "Reason has too many characters (100000)"},
            {field: "calculated", rule: (v: any) => !!v || "calculated is required"},
            {field: "calculated", rule: (v: any) => v.validate()},
    ];
}


export class StockTakeLineReadOnly extends ModelObject<StockTakeLineReadOnly> {

    /**
    * Quantity BrewMan believed was in stock at the time stock count was started.
    */
    public frozenStockLevel: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.frozenStockLevel = jsonSerializedObject.frozenStockLevel;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.frozenStockLevel = this.frozenStockLevel;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class StockTakeLine extends ModelObject<StockTakeLine> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Stock Take to which this line belongs.
    */
    public stockTakeId: Guid = Guid.createEmpty();
    private _countedStockLevel: number | null = null;
    /**
    * Quantity Counted as physically in stock at the time stock count was started, adjusted for any movements which had not been processed in BrewMan by that time.
    */
    public get countedStockLevel(): number | null { return this._countedStockLevel }
    public set countedStockLevel(value: number | null) {
      this._countedStockLevel = value === undefined ? null : value;
    }
    /**
    * Batch of stock which is counted by this line.
    */
    public stockBatchId: Guid = Guid.createEmpty();
    /**
    * Unit of measure in which this line is counting. Taken from the stock item.
    */
    public unitOfMeasureId: Guid = Guid.createEmpty();
    public readOnly: StockTakeLineReadOnly = new StockTakeLineReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.stockTakeId = Guid.parseOrEmpty(jsonSerializedObject.stockTakeId);
          this.countedStockLevel = jsonSerializedObject.countedStockLevel == null ? null : jsonSerializedObject.countedStockLevel;
          this.stockBatchId = Guid.parseOrEmpty(jsonSerializedObject.stockBatchId);
          this.unitOfMeasureId = Guid.parseOrEmpty(jsonSerializedObject.unitOfMeasureId);
          this.readOnly = new StockTakeLineReadOnly(jsonSerializedObject.readOnly);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.stockTakeId = this.stockTakeId.toString();
        toRet.countedStockLevel = this.countedStockLevel == null ? null : this.countedStockLevel;
        toRet.stockBatchId = this.stockBatchId.toString();
        toRet.unitOfMeasureId = this.unitOfMeasureId.toString();
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "StockTakeLine.Id cannot be empty"},
            {field: "stockTakeId", rule: (v: any) => !v.isEmpty() || "StockTakeLine.StockTakeId cannot be empty"},
            {field: "stockBatchId", rule: (v: any) => !v.isEmpty() || "StockTakeLine.StockBatchId cannot be empty"},
            {field: "unitOfMeasureId", rule: (v: any) => !v.isEmpty() || "StockTakeLine.UnitOfMeasureId cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class StockTake extends BaseModelObject<StockTake> {

    public header: StockTakeHeader = new StockTakeHeader();
    public lines: StockTakeLine[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.header = new StockTakeHeader(jsonSerializedObject.header);
          this.lines = jsonSerializedObject.lines.map((v: any) => new StockTakeLine(v));
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.header = this.header.toJsonSerializedObject();
        toRet.lines = this.lines.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "header", rule: (v: any) => !!v || "header is required"},
            {field: "header", rule: (v: any) => v.validate()},
            {field: "lines", rule: (v: any) => !!v || "lines is required"},
            {field: "lines", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as StockTakeLine[]).reduce((acc: Array<string>, curr: StockTakeLine) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class StockTakeFilter extends ModelObject<StockTakeFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _stockLocationIds: Guid[] | null = null;
    public get stockLocationIds(): Guid[] | null { return this._stockLocationIds }
    public set stockLocationIds(value: Guid[] | null) {
      this._stockLocationIds = value === undefined ? null : value;
    }
    private _completedFrom: LocalDate | null = null;
    public get completedFrom(): LocalDate | null { return this._completedFrom }
    public set completedFrom(value: LocalDate | null) {
      this._completedFrom = value === undefined ? null : value;
    }
    private _completedTo: LocalDate | null = null;
    public get completedTo(): LocalDate | null { return this._completedTo }
    public set completedTo(value: LocalDate | null) {
      this._completedTo = value === undefined ? null : value;
    }
    private _statuses: EnumStockTakeStatus[] | null = null;
    public get statuses(): EnumStockTakeStatus[] | null { return this._statuses }
    public set statuses(value: EnumStockTakeStatus[] | null) {
      this._statuses = value === undefined ? null : value;
    }
    private _stockTakeIds: Guid[] | null = null;
    public get stockTakeIds(): Guid[] | null { return this._stockTakeIds }
    public set stockTakeIds(value: Guid[] | null) {
      this._stockTakeIds = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.stockLocationIds = jsonSerializedObject.stockLocationIds == null ? null : jsonSerializedObject.stockLocationIds.map((v: any) => Guid.parseOrEmpty(v));
          this.completedFrom = jsonSerializedObject.completedFrom == null ? null : LocalDate.parse(jsonSerializedObject.completedFrom);
          this.completedTo = jsonSerializedObject.completedTo == null ? null : LocalDate.parse(jsonSerializedObject.completedTo);
          this.statuses = jsonSerializedObject.statuses == null ? null : jsonSerializedObject.statuses.map((v: any) => EnumStockTakeStatus.getByValue(v));
          this.stockTakeIds = jsonSerializedObject.stockTakeIds == null ? null : jsonSerializedObject.stockTakeIds.map((v: any) => Guid.parseOrEmpty(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.stockLocationIds = this.stockLocationIds == null ? null : this.stockLocationIds.map(v => v.toString());
        toRet.completedFrom = this.completedFrom == null ? null : this.completedFrom.toString();
        toRet.completedTo = this.completedTo == null ? null : this.completedTo.toString();
        toRet.statuses = this.statuses == null ? null : this.statuses.map(v => v.value);
        toRet.stockTakeIds = this.stockTakeIds == null ? null : this.stockTakeIds.map(v => v.toString());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
    ];
}


export class FilteredStockTakeHeaders extends ModelObject<FilteredStockTakeHeaders> {

    public matchingCount: number = 0;
    public stockTakeHeaders: StockTakeHeader[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.stockTakeHeaders = jsonSerializedObject.stockTakeHeaders.map((v: any) => new StockTakeHeader(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.stockTakeHeaders = this.stockTakeHeaders.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "stockTakeHeaders", rule: (v: any) => !!v || "stockTakeHeaders is required"},
            {field: "stockTakeHeaders", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as StockTakeHeader[]).reduce((acc: Array<string>, curr: StockTakeHeader) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class FilteredStockTakes extends ModelObject<FilteredStockTakes> {

    public matchingCount: number = 0;
    public stockTakes: StockTake[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.stockTakes = jsonSerializedObject.stockTakes.map((v: any) => new StockTake(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.stockTakes = this.stockTakes.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "stockTakes", rule: (v: any) => !!v || "stockTakes is required"},
            {field: "stockTakes", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as StockTake[]).reduce((acc: Array<string>, curr: StockTake) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class StockTakeService extends ServiceBase {

  public async saveStockTakeHeader(tenantId: Guid, header: StockTakeHeader, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<StockTake> {
    const req = {
    tenantId:tenantId.toString(),
    header:header.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new StockTake(response.stockTake);;
    return this.makeJsonRequest("stockTake/v1/saveStockTakeHeader", req, true, extractor, opts);
  };

  public async saveStockTakeLines(tenantId: Guid, stockTakeId: Guid, lines: readonly StockTakeLine[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<StockTake> {
    const req = {
    tenantId:tenantId.toString(),
    stockTakeId:stockTakeId.toString(),
    lines:lines.map(v => v.toJsonSerializedObject()),
    };
    const extractor = (response:any) => new StockTake(response.stockTake);;
    return this.makeJsonRequest("stockTake/v1/saveStockTakeLines", req, true, extractor, opts);
  };

  public async getStockTake(tenantId: Guid, stockTakeId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<StockTake> {
    const req = {
    tenantId:tenantId.toString(),
    stockTakeId:stockTakeId.toString(),
    };
    const extractor = (response:any) => new StockTake(response.stockTake);;
    return this.makeJsonRequest("stockTake/v1/getStockTake", req, false, extractor, opts);
  };

  public async getStockTakes(tenantId: Guid, stockTakeIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<StockTake[]> {
    const req = {
    tenantId:tenantId.toString(),
    stockTakeIds:stockTakeIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.stockTakes.map((v: any) => new StockTake(v));;
    return this.makeJsonRequest("stockTake/v1/getStockTakes", req, false, extractor, opts);
  };

  public async getAllStockTakeHeaders(tenantId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<StockTakeHeader[]> {
    const req = {
    tenantId:tenantId.toString(),
    };
    const extractor = (response:any) => response.headers.map((v: any) => new StockTakeHeader(v));;
    return this.makeJsonRequest("stockTake/v1/getAllStockTakeHeaders", req, false, extractor, opts);
  };

  public async cancelStockTake(tenantId: Guid, stockTakeId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    stockTakeId:stockTakeId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("stockTake/v1/cancelStockTake", req, true, extractor, opts);
  };

  public async startStockTake(tenantId: Guid, stockTakeId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<StockTake> {
    const req = {
    tenantId:tenantId.toString(),
    stockTakeId:stockTakeId.toString(),
    };
    const extractor = (response:any) => new StockTake(response.stockTake);;
    return this.makeJsonRequest("stockTake/v1/startStockTake", req, true, extractor, opts);
  };

  public async completeStockTake(tenantId: Guid, stockTakeId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    stockTakeId:stockTakeId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("stockTake/v1/completeStockTake", req, true, extractor, opts);
  };

  public async generateStockTakeSheet(tenantId: Guid, documentFormat: EnumDocumentFileType, stockTakeId: Guid, documentTemplateId: Guid, quantity: number, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Uint8Array> {
    const req = {
    tenantId:tenantId.toString(),
    documentFormat:documentFormat.value,
    stockTakeId:stockTakeId.toString(),
    documentTemplateId:documentTemplateId.toString(),
    quantity:quantity,
    };
    const extractor = (response:any) => converters.base64ToUint8Array(response.documentBytes);;
    return this.makeJsonRequest("stockTake/v1/generateStockTakeSheet", req, false, extractor, opts);
  };

  public async getStockTakeHeaders(tenantId: Guid, stockTakeIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<StockTakeHeader[]> {
    const req = {
    tenantId:tenantId.toString(),
    stockTakeIds:stockTakeIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.headers.map((v: any) => new StockTakeHeader(v));;
    return this.makeJsonRequest("stockTake/v1/getStockTakeHeaders", req, false, extractor, opts);
  };

  public async getStockTakeHeadersByFilter(tenantId: Guid, filter: StockTakeFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredStockTakeHeaders> {
    const req = {
    tenantId:tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredStockTakeHeaders(response.results);;
    return this.makeJsonRequest("stockTake/v1/getStockTakeHeadersByFilter", req, false, extractor, opts);
  };

  public async getStockTakesByFilter(tenantId: Guid, filter: StockTakeFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredStockTakes> {
    const req = {
    tenantId:tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredStockTakes(response.results);;
    return this.makeJsonRequest("stockTake/v1/getStockTakesByFilter", req, false, extractor, opts);
  };
}

export const stockTakeService = new StockTakeService();